import QuickNewsIcon from "@assets/quick-news/QnIcon.svg";
import { QUICKNEWS_FLAG } from "@helper/getEnvVariables";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { isClientSide } from "@helper/utils";
import Cookies from "js-cookie";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

// Constants
const CANVAS_DIMENSIONS = {
  LIGHTNING: { width: 40, height: 45 },
  HAND_GESTURE: { width: 124, height: 84 },
} as const;

const ANIMATION_PATHS = {
  LIGHTNING: "quick-news/lightning.lottie",
  HAND_GESTURE: "quick-news/handGesture.lottie",
} as const;

const HAND_GESTURE_TIMEOUT = 5000;

/** -----------------------
 *  Hooks
 * ----------------------- */
const useViewportHeight = () => {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!isClientSide) return;

    setHeight(window.innerHeight);

    const onResize = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return height;
};

/**
 * Hide/show floating button based on scroll direction & threshold
 */
const useScrollVisibility = (threshold: number) => {
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const updateVisibility = () => {
      const currentY = window.scrollY;

      if (currentY === 0) {
        setVisible(true);
      } else if (currentY > lastScrollY.current && currentY >= threshold) {
        setVisible(false);
      } else if (currentY < lastScrollY.current && currentY < threshold) {
        setVisible(true);
      }

      lastScrollY.current = currentY;
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updateVisibility);
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [threshold]);

  return visible;
};

/**
 * Load & clean up a Lottie animation
 */
const useLottie = (config: LottieConfig) => {
  const instanceRef = useRef<LottieInstance | null>(null);

  useEffect(() => {
    instanceRef.current = loadLottieAnimation(config);

    return () => {
      instanceRef.current?.destroy();
      instanceRef.current = null;
    };
  }, [config]);

  return instanceRef;
};

/** -----------------------
 *  Components
 * ----------------------- */
export function QnHeaderIcon() {
  return (
    <Link
      to="/quick-news?ref=top-menu-quick-news"
      data-testid="QuickNewsHeaderIcon"
      onClick={() => {
        Cookies.set("qnRedirectUrl", encodeURIComponent(window.location.href));
      }}
      className="focus:shadow-5xl m-auto"
    >
      <img src={QuickNewsIcon} alt="quick-news-icon" width="32" height="32" />
    </Link>
  );
}

export function QnFloatButton({ refCode }: { refCode: string }) {
  const shouldRender = QUICKNEWS_FLAG && typeof window !== "undefined";

  const canvasId = "lightning-canvas";

  // Hooks MUST always run
  const viewportHeight = useViewportHeight();
  const isVisible = useScrollVisibility(viewportHeight);

  const lightningConfig = useMemo<LottieConfig>(
    () => ({
      animationPath: ANIMATION_PATHS.LIGHTNING,
      canvasId,
      dimensions: CANVAS_DIMENSIONS.LIGHTNING,
    }),
    [canvasId],
  );

  useLottie(lightningConfig);

  // Return early AFTER hooks run
  if (!shouldRender) return null;

  return (
    <div
      className={twMerge(
        "quick-news-float fixed bottom-[110px] md:bottom-20 right-4 lg:right-[calc(-600px+(0.5*100svw))] z-50 transition-all duration-1000",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-24",
      )}
    >
      <Link
        to={`/quick-news?ref=${refCode}`}
        className="w-[56px] lg:w-[140px] h-[46px] flex items-center justify-center rounded-[5px] bg-gradient-to-br from-pink-200 to-pink-1100 shadow-[0_4px_4px_rgba(0,0,0,0.15)]"
        onClick={() => {
          Cookies.set(
            "qnRedirectUrl",
            encodeURIComponent(window.location.href),
          );
        }}
      >
        <div className="px-1 relative flex items-center justify-center mb-2">
          <canvas id={canvasId} className="w-[40px]" />
        </div>

        <div className="hidden lg:inline text-white-100 font-bold text-3xs leading-[120%] pr-2">
          {"முன்னோட்டம்"}
        </div>
      </Link>
    </div>
  );
}

export function QnHandGesture({ canvasId }: { canvasId: string }) {
  const [visible, setVisible] = useState(true);

  useLottie({
    animationPath: ANIMATION_PATHS.HAND_GESTURE,
    canvasId,
    dimensions: CANVAS_DIMENSIONS.HAND_GESTURE,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, HAND_GESTURE_TIMEOUT);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (!visible) return null;

  return (
    <div className="absolute z-30 top-[calc(50%-62px)] left-[calc(50%-62px)]">
      <canvas id={canvasId} className="w-[124px]" />
    </div>
  );
}
